/**
 * 浏览器端 RSA-SHA1 签名工具。
 * 私钥只从管理接口取入浏览器，签名在 Web Crypto 中完成，业务数据直发球端。
 */

/**
 * 清理 Base64/PEM 格式密钥。私钥使用 PKCS#8 DER Base64 文本；
 * 同时接受常见的 `BEGIN PRIVATE KEY` 包装，但不伪装支持 PKCS#1。
 */
export function parsePemKey(pemKey, type = 'private') {
  if (typeof pemKey !== 'string' || !pemKey.trim()) {
    throw new Error('密钥为空');
  }

  const input = pemKey.trim();
  if (type === 'private' && /-----BEGIN RSA PRIVATE KEY-----/.test(input)) {
    throw new Error('仅支持 PKCS#8 私钥（BEGIN PRIVATE KEY），不支持 PKCS#1 私钥');
  }
  if (type === 'public' && /-----BEGIN RSA PUBLIC KEY-----/.test(input)) {
    throw new Error('仅支持 SPKI 公钥（BEGIN PUBLIC KEY），不支持 PKCS#1 公钥');
  }

  const key = input
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/[\s\r\n]/g, '');

  if (!key || !/^[A-Za-z0-9+/]*={0,2}$/.test(key) || key.length % 4 === 1) {
    throw new Error('密钥不是有效的 Base64 编码');
  }

  // 浏览器 atob 需要补齐 Base64。
  return key + (key.length % 4 ? '='.repeat(4 - (key.length % 4)) : '');
}

/**
 * Base64 解码为 ArrayBuffer
 */
export function base64ToArrayBuffer(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * ArrayBuffer 编码为 Base64
 */
export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * PEM 私钥导入为 CryptoKey
 */
export async function importPrivateKey(pemKey) {
  const cleanKey = parsePemKey(pemKey, 'private');
  const keyBytes = base64ToArrayBuffer(cleanKey);
  
  // PKCS#8 格式
  return window.crypto.subtle.importKey(
    'pkcs8',
    keyBytes,
    {
      name: 'RSASSA-PKCS1-v1_5',
      hash: { name: 'SHA-1' }
    },
    false,
    ['sign']
  );
}

/**
 * PEM 公钥导入为 CryptoKey
 */
export async function importPublicKey(pemKey) {
  const cleanKey = parsePemKey(pemKey, 'public');
  const keyBytes = base64ToArrayBuffer(cleanKey);
  
  // SPKI 格式
  return window.crypto.subtle.importKey(
    'spki',
    keyBytes,
    {
      name: 'RSASSA-PKCS1-v1_5',
      hash: { name: 'SHA-1' }
    },
    false,
    ['verify']
  );
}

/**
 * 签名数据。
 * Web Crypto 生成原始 RSA 签名，再编码为 Base64 文本的 ASCII 字节。
 * 该 Uint8Array 可直接写入 protobuf SignedMessage.signature。
 */
export async function signData(privateKey, data) {
  const rawSignature = await window.crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    privateKey,
    data
  );
  const base64 = arrayBufferToBase64(rawSignature);
  const bytes = new Uint8Array(base64.length);
  for (let i = 0; i < base64.length; i++) bytes[i] = base64.charCodeAt(i);
  // 调试输出实际 wire 长度：不输出私钥或签名内容，只输出字节数。
  console.log('[SignUtils] 签名长度', {
    rawSignatureBytes: rawSignature.byteLength,
    base64TextLength: base64.length,
    signatureFieldBytes: bytes.byteLength
  });
  return bytes;
}

/**
 * 验证 兼容的 signature 字段。
 * wire 中先是 Base64 文本 ASCII 字节，验签前还原为原始 RSA 签名字节。
 */
export async function verifySignature(publicKey, data, signatureBytes) {
  const signatureText = String.fromCharCode(...new Uint8Array(signatureBytes));
  const rawSignature = base64ToArrayBuffer(signatureText);
  return window.crypto.subtle.verify(
    'RSASSA-PKCS1-v1_5',
    publicKey,
    rawSignature,
    data
  );
}

/**
 * 生成 UUID v4
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * 生成随机 ID（用于 signaling clientId）
 */
export function generateRandomId(length = 10) {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default {
  parsePemKey,
  base64ToArrayBuffer,
  arrayBufferToBase64,
  importPrivateKey,
  importPublicKey,
  signData,
  verifySignature,
  generateUUID,
  generateRandomId
};
